/**
 * IncomingCallPopup
 * ─────────────────────────────────────────────────────────────────────────────
 * Floating popup that appears on all CC agent screens when an inbound Exotel
 * call arrives on the virtual ExoPhone number.
 *
 * Actions:
 *   1. "Register Lead"           — Navigates directly to /leads/create with the 10-digit
 *                                   phone number auto-filled and lookup triggered.
 *   2. "Existing Lead for Query" — Opens an inline query box to log why the caller
 *                                   called (reason + notes) and saves it to CallLog history.
 *   3. "Dismiss"                 — Closes the popup.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Phone,
  PhoneOff,
  PhoneIncoming,
  User,
  MessageSquare,
  Check,
  X,
  Loader2,
  FileText,
  ArrowLeft,
  Send,
  Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import { saveCallQuery } from '../../services/exotelApi';

const DISMISS_AFTER_MS = 30_000; // 30 seconds auto-dismiss

// Preset categories for fast logging
const PRESET_REASONS = [
  'Doctor Consultation',
  'Appointment Query',
  'Lab / Test Reports',
  'Billing / Insurance',
  'General Inquiry',
  'Complaint / Issue'
];

// ── Ringtone (Web Audio API, no external file needed) ─────────────────────────
const playRingTone = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playBeep = (startTime) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, startTime);
      gain.gain.setValueAtTime(0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.6);
      osc.start(startTime);
      osc.stop(startTime + 0.6);
    };
    // Two beeps
    playBeep(ctx.currentTime);
    playBeep(ctx.currentTime + 0.8);
    return ctx;
  } catch {
    // Silently fail if AudioContext unavailable
    return null;
  }
};

// ── Single Call Card Component ────────────────────────────────────────────────
const CallCard = ({ call, onNavigate, onRegister, onDismiss }) => {
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(DISMISS_AFTER_MS / 1000));
  const [isLoggingQuery, setIsLoggingQuery] = useState(false);
  const [selectedReason, setSelectedReason] = useState('Doctor Consultation');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const timerRef = useRef(null);

  // Auto-dismiss countdown timer (pauses when logging query)
  useEffect(() => {
    if (isLoggingQuery) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          onDismiss(call.callSid);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [call.callSid, isLoggingQuery, onDismiss]);

  // Handle saving query/reason to CallLog
  const handleSaveQuery = async () => {
    const finalReason = selectedReason === 'Other'
      ? (customReason.trim() || 'Other Query')
      : (selectedReason || customReason.trim() || 'General Inquiry');

    setIsSaving(true);
    try {
      const targetId = call.callSid || call.callLogId;
      if (!targetId) {
        toast.error('No call identifier found to save query');
        return;
      }

      await saveCallQuery(targetId, {
        callReason: finalReason,
        notes: notes.trim()
      });

      toast.success('Query saved to call history!', { icon: '📝' });
      onDismiss(call.callSid);
    } catch (err) {
      console.error('Failed to save call query:', err);
      toast.error(err?.response?.data?.message || 'Failed to save query reason');
    } finally {
      setIsSaving(false);
    }
  };

  // Progress ring
  const progress = (secondsLeft / (DISMISS_AFTER_MS / 1000)) * 100;
  const circumference = 2 * Math.PI * 12;

  return (
    <div
      className={`relative rounded-3xl overflow-hidden shadow-2xl transition-all duration-300 animate-slide-in-right pointer-events-auto ${
        isLoggingQuery ? 'w-[420px] max-w-[92vw]' : 'w-[380px] max-w-[92vw]'
      }`}
      style={{
        background: 'linear-gradient(135deg, rgba(15,17,23,0.98) 0%, rgba(20,24,35,0.98) 100%)',
        border: '1px solid rgba(124,58,237,0.4)',
        backdropFilter: 'blur(24px)',
        boxShadow: '0 25px 60px rgba(0,0,0,0.75), 0 0 0 1px rgba(124,58,237,0.25), inset 0 1px 0 rgba(255,255,255,0.08)'
      }}
    >
      {/* Animated top shimmer border */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{
          background: 'linear-gradient(90deg, transparent, #7c3aed, #6366f1, #7c3aed, transparent)',
          animation: 'shimmer 2s linear infinite',
          backgroundSize: '200% 100%'
        }}
      />

      {/* Dismiss button */}
      <button
        onClick={() => onDismiss(call.callSid)}
        className="absolute top-3.5 right-3.5 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all z-10"
        title="Dismiss"
      >
        <X size={13} className="text-white/50 hover:text-white" />
      </button>

      {/* ─── MODE 1: QUERY LOGGING POPUP ──────────────────────────────────── */}
      {isLoggingQuery ? (
        <div className="p-6 animate-fadeIn">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
            <button
              onClick={() => setIsLoggingQuery(false)}
              className="w-8 h-8 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center transition-all"
              title="Back"
            >
              <ArrowLeft size={15} />
            </button>
            <div>
              <h4 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-1.5">
                <MessageSquare size={14} className="text-indigo-400" />
                Existing Lead Query
              </h4>
              <p className="text-xs text-white/50 font-medium">
                {call.patientName !== 'Unknown Caller' ? call.patientName : 'Caller'} &bull;{' '}
                <span className="font-mono text-white/70">{call.callerNumber}</span>
              </p>
            </div>
          </div>

          {/* Quick Category Chips */}
          <div className="mb-3">
            <label className="text-[10px] font-black text-indigo-300 uppercase tracking-widest block mb-2">
              Reason for Calling *
            </label>
            <div className="flex flex-wrap gap-1.5">
              {PRESET_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    setSelectedReason(r);
                    setCustomReason('');
                  }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                    selectedReason === r
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/50 scale-[1.02]'
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {r}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setSelectedReason('Other')}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                  selectedReason === 'Other'
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-900/50 scale-[1.02]'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                Other
              </button>
            </div>
          </div>

          {/* Custom Reason input if "Other" */}
          {selectedReason === 'Other' && (
            <div className="mb-3">
              <input
                type="text"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Type specific call reason..."
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white placeholder-white/30 focus:outline-none focus:border-indigo-500"
                autoFocus
              />
            </div>
          )}

          {/* Detailed Notes */}
          <div className="mb-4">
            <label className="text-[10px] font-black text-indigo-300 uppercase tracking-widest block mb-1.5">
              Query Discussion Notes / Remarks
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What did the patient ask or discuss during this call?"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white placeholder-white/30 focus:outline-none focus:border-indigo-500 resize-none font-sans leading-relaxed"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleSaveQuery}
              disabled={isSaving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all bg-indigo-600 hover:bg-indigo-500 shadow-lg shadow-indigo-900/50 disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Send size={13} />
                  Save to Call History
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => setIsLoggingQuery(false)}
              disabled={isSaving}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold text-white/60 hover:text-white bg-white/5 hover:bg-white/10 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        /* ─── MODE 2: DEFAULT INCOMING CALL CARD ────────────────────────── */
        <div className="p-6">
          {/* Top row: icon + countdown timer */}
          <div className="flex items-start justify-between mb-4">
            {/* Pulsing incoming phone icon */}
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                <PhoneIncoming size={22} className="text-violet-400 animate-bounce" />
              </div>
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-[#0f1117] animate-pulse" />
            </div>

            {/* Countdown ring */}
            <svg width="32" height="32" className="rotate-[-90deg]">
              <circle cx="16" cy="16" r="14" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="3" />
              <circle
                cx="16"
                cy="16"
                r="14"
                fill="none"
                stroke="#7c3aed"
                strokeWidth="3"
                strokeDasharray={circumference * (14 / 12)}
                strokeDashoffset={circumference * (14 / 12) * (1 - progress / 100)}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
          </div>

          {/* Title */}
          <p className="text-[11px] font-black text-violet-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
            <Phone size={11} />
            Incoming Call
          </p>

          {/* Patient name */}
          <p className="text-xl font-bold text-white leading-tight mb-1">
            {call.patientName !== 'Unknown Caller' ? call.patientName : 'Unknown Caller'}
          </p>

          {/* Caller number */}
          <p className="text-sm text-white/50 mb-3.5 font-mono">
            {call.callerNumber || '—'}
          </p>

          {/* Lead link status badge */}
          {call.leadId ? (
            <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <User size={13} className="text-emerald-400 flex-shrink-0" />
              <span className="text-xs text-emerald-300 font-semibold">Lead matched in CRM</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <User size={13} className="text-amber-400 flex-shrink-0" />
              <span className="text-xs text-amber-300 font-semibold">New number — no lead found</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              {/* Button 1: Register Lead */}
              <button
                onClick={() => onRegister(call.callerNumber, call.patientName, call.callSid)}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold text-white transition-all bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-900/40 active:scale-[0.98]"
                title="Open registration page with number pre-filled"
              >
                <User size={14} />
                Register Lead
              </button>

              {/* Button 2: Existing Lead for Query */}
              <button
                onClick={() => setIsLoggingQuery(true)}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold text-white transition-all bg-indigo-600 hover:bg-indigo-500 shadow-lg shadow-indigo-900/40 active:scale-[0.98]"
                title="Log query reason and notes for this caller"
              >
                <MessageSquare size={14} />
                Existing Lead for Query
              </button>
            </div>

            {/* Button 3: Dismiss */}
            <button
              onClick={() => onDismiss(call.callSid)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-white/50 hover:text-white bg-white/5 hover:bg-white/10 transition-all active:scale-[0.98]"
            >
              <PhoneOff size={13} />
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Popup Manager ────────────────────────────────────────────────────────

const IncomingCallPopup = ({ incomingCalls = [], onDismiss }) => {
  const navigate = useNavigate();

  const handleNavigate = useCallback((leadId, callSid) => {
    onDismiss(callSid);
    navigate(`/leads/${leadId}`);
  }, [navigate, onDismiss]);

  const handleRegister = useCallback((callerNumber, patientName, callSid) => {
    onDismiss(callSid);
    // Sanitize caller phone to 10 digits
    const cleanPhone = String(callerNumber || '').replace(/\D/g, '').slice(-10);
    const validName = patientName && patientName !== 'Unknown Caller' ? patientName : '';
    navigate(`/leads/create?phone=${encodeURIComponent(cleanPhone)}&name=${encodeURIComponent(validName)}`);
  }, [navigate, onDismiss]);

  if (incomingCalls.length === 0) return null;

  return createPortal(
    <div
      className="fixed bottom-5 right-5 z-[9999] flex flex-col items-end gap-3 pointer-events-none max-h-[calc(100vh-2.5rem)] overflow-y-auto p-2"
    >
      {incomingCalls.map((call) => (
        <CallCard
          key={call.callSid || call.callLogId}
          call={call}
          onNavigate={handleNavigate}
          onRegister={handleRegister}
          onDismiss={onDismiss}
        />
      ))}
    </div>,
    document.body
  );
};

// ── CSS animation injection (no external CSS file needed) ─────────────────────
if (typeof document !== 'undefined' && !document.getElementById('exotel-popup-styles')) {
  const style = document.createElement('style');
  style.id = 'exotel-popup-styles';
  style.textContent = `
    @keyframes slide-in-right {
      from { transform: translateX(120%); opacity: 0; }
      to   { transform: translateX(0);   opacity: 1; }
    }
    .animate-slide-in-right {
      animation: slide-in-right 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    }
    @keyframes shimmer {
      0%   { background-position: 200% center; }
      100% { background-position: -200% center; }
    }
    @keyframes fadeIn {
      from { opacity: 0; transform: scale(0.98); }
      to   { opacity: 1; transform: scale(1); }
    }
    .animate-fadeIn {
      animation: fadeIn 0.25s ease-out forwards;
    }
  `;
  document.head.appendChild(style);
}

export { IncomingCallPopup, playRingTone };
